import { Location } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ESidebarMode } from 'app/enums/sidebarmode.enum';
import * as IAction from 'app/interfaces/actiontracker.interface';
import { IEvent } from 'app/interfaces/event.interface';
import { ISidebar } from 'app/interfaces/sidebar.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { EventsService } from 'app/services/events.service';
import { NotificationsService } from 'app/services/notifications.service';
import dayjs from 'dayjs';
import { AppointmentSidebarService } from 'modules/appointments/services/appointment-sidebar.service';
import { IAppointment } from 'shared/interfaces/appointment.interface';
import { AppointmentService } from 'shared/services/appointment/appointment.service';

import { UserService } from '../../../../app/services/user.service';
import { PromptuarySidebarService } from '../../../promptuary/services/sidebar/promptuary-sidebar.service';

@Component({
	selector: 'contact-promptuaries',
	templateUrl: './contact-promptuaries.component.html',
	styleUrls: ['./contact-promptuaries.component.scss'],
})
export class ContactPromptuaries implements OnInit, OnChanges {
	professionalId = null;
	loading = true;
	loadingMore = false;
	loadingConfirmNoShow = false;
	noShowClickIndex: number;
	pageSize = {
		default: 8,
		actual: 8,
	};
	appointments = {
		list: [],
		total: 0,
	};
	activeSidebar: ISidebar;

	@Input() filter: 'by_all' | 'by_me' | 'by_others' | null = null;
	@Input() clientId: string;
	@Output('newAppointment') newAppointment = new EventEmitter();

	readonly noShowToggleId = 'noShow-checkbox';

	constructor(
		public actionTrackerService: ActionTrackerService,
		public appointmentService: AppointmentService,
		public location: Location,
		public notificationService: NotificationsService,
		public router: Router,
		public translateService: TranslateService,
		public user: UserService,
		public promptuarySidebarService: PromptuarySidebarService,
		public events: EventsService,
		public appointmentSidebarService: AppointmentSidebarService
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.filter) {
			this.loading = true;
			this.resetList();
			this.fetchAppointments(this.clientId);
		}
	}

	ngOnInit(): void {
		//Atualiza o item da lista quando sidebar-promptuary emite evento de registro de prontuário com mesmo id
		this.promptuarySidebarService.registerObservable.subscribe(
			(registeredAppointmentId) => {
				if (registeredAppointmentId) {
					this.appointments.list = this.appointments.list.map((appointment) => {
						if (registeredAppointmentId === appointment.id) {
							return {
								...appointment,
								hasPromptuary: true,
							};
						}

						return appointment;
					});
				}
			}
		);

		this.promptuarySidebarService.noShowObservable.subscribe((appointment) => {
			if (appointment) {
				this.appointments.list = this.appointments.list.map((item) => {
					if (item.id === appointment.appointmentId) {
						return {
							...item,
							noShow: appointment.noShow,
							noShowReasonId: appointment.reasonId,
							notEditable: appointment.notEditable,
						};
					}
					return item;
				});
			}
		});

		//
		this.user.getProfileData().then((profile) => {
			this.professionalId = profile.id;
		});
		if (this.clientId) {
			this.fetchAppointments(this.clientId);
		}
	}

	fetchAppointments(clientId: string, pageSize?: number) {
		const limit = pageSize || this.pageSize.default;

		this.appointmentService
			.getPromptuaries(clientId, limit.toString(), this.filter)
			.subscribe(
				({ items, total }) => {
					this.appointments.list = items.map((item) => {
						const priceToShow = this.events.getEventPriceToShow(
							item as unknown as IEvent
						);
						return {
							...item,
							priceToShow: priceToShow.value,
							priceFinalCompensation: priceToShow.valueNumber,
							formattedProfessionalName: this.getProfessionalName(
								item.professionalName,
								item.professionalMainType
							),
							notEditable: item.notEditable || item.noShow,
						};
					});
					this.appointments.total = total;
					this.loading = false;
					this.loadingMore = false;
				},
				() => {
					this.notificationService.showError(
						this.translateService.instant('CONTACT.ERRORS.REQUEST_ERROR')
					);
				}
			);
	}

	redirectToRegisteredPromptuary(appointment: IAppointment) {
		this.actionTrackerService.eventTracker(
			IAction.SCOPE_TYPE.Promptuary,
			IAction.SUBSCOPE_TYPE.Session,
			IAction.ITEM_TYPE.BtnView,
			IAction.INTERACTION_TYPE.Click,
			{}
		);

		this.router.navigate([`/promptuary/${appointment.id}/${this.clientId}`], {
			state: { returnTo: this.location.path() },
		});
	}

	openRegisterPromptuarySidebar(appointment) {
		this.actionTrackerService.eventTracker(
			IAction.SCOPE_TYPE.Promptuary,
			IAction.SUBSCOPE_TYPE.Session,
			IAction.ITEM_TYPE.BtnRegister,
			IAction.INTERACTION_TYPE.Click,
			{}
		);
		this.promptuarySidebarService.openSidebarOrGoToPage(
			appointment.profileId,
			appointment.id,
			'appointment-list'
		);
	}

	loadMore() {
		this.loadingMore = true;
		this.pageSize.actual = this.pageSize.actual + this.pageSize.default;
		this.fetchAppointments(this.clientId, this.pageSize.actual);
	}

	setAppointmentAsNoshow(status: boolean, itemIndex: any) {
		if (status && this.noShowClickIndex < 0) {
			this.noShowClickIndex = itemIndex;
			return;
		} else if (status) {
			this.loadingConfirmNoShow = true;
		}

		const appointment = this.appointments.list[itemIndex];
		if (appointment.notEditable) {
			return;
		}
		appointment.loading = true;

		this.appointmentService
			.setNoshow(
				appointment.id,
				status,
				status ? 'no-show:client-no-show' : null
			)
			.subscribe(
				() => {
					appointment.noShow = status;
					appointment.loading = false;
					if (appointment.noShow) {
						this.noShowClickIndex = -1;
						appointment.notEditable = true;
						this.notificationService.showSuccess(
							this.translateService.instant('SCHEDULE.SEND_REPOSITION_INVITE'),
							this.translateService.instant('SCHEDULE.CONFIRMED_NO')
						);
						this.loadingConfirmNoShow = false;
					}
				},
				({ error }) => {
					let message = '';

					if (error.message === 'EventNotStarted') {
						message = this.translateService.instant(
							'CONTACT.ERRORS.NOT_HAPPENED'
						);
					}
					appointment.noShow = !status;
					appointment.loading = false;
					this.loadingConfirmNoShow = false;

					this.notificationService.showError(message || error.messageI18n);
				}
			);
	}

	getProfessionalName(name, jobTitle) {
		let translatedJobTitle = '';
		if (jobTitle) {
			translatedJobTitle = this.translateService.instant(
				`CONTACT.PROFESSIONAL_TYPE.${jobTitle.toUpperCase()}`
			);
		}
		return `${name} (${translatedJobTitle})`;
	}

	resetList() {
		this.appointments.list = [];
		this.appointments.total = 0;
		this.pageSize = {
			default: 8,
			actual: 8,
		};
		this.noShowClickIndex = -1;
	}
	async scheduleNowShowAppointment(appointment: IAppointment) {
		this.actionTrackerService.eventTracker(
			IAction.SCOPE_TYPE.Contact,
			IAction.SUBSCOPE_TYPE.Reposition,
			IAction.ITEM_TYPE.SendInviteBtn,
			IAction.INTERACTION_TYPE.Click,
			{
				contactId: appointment.contactId,
				professionalId: appointment.professionalId,
				eventId: appointment.id,
			}
		);

		this.activeSidebar = {
			mode: ESidebarMode.New,
			contactId: Number(appointment.contactId),
			isReposition: true,
		};

		await this.appointmentSidebarService.setSidebar(true, this.activeSidebar);
	}

	validateShowToScheduleNoShowButton(item: IAppointment): boolean {
		const day = dayjs(item.day);
		return item.noShow && day.isValid() && dayjs().diff(day, 'day') <= 90;
	}

	formatNoShowId(index: number) {
		return `${this.noShowToggleId}-${index}`;
	}

	closeNoShowModal() {
		if (this.noShowClickIndex < 0) {
			return;
		}

		const noShowId = this.formatNoShowId(this.noShowClickIndex);
		const noShowToggleElem = document.getElementById(noShowId) as any;
		if (noShowToggleElem) {
			noShowToggleElem.checked = false;
		} else {
			console.error(`noShow toggle ${noShowId} was not found but should have!`);
		}

		this.noShowClickIndex = -1;
	}

	validateIfRepositionInviteAlreadySent(appointment: IAppointment): boolean {
		return !!appointment.reposition?.eventId?.length;
	}
}
